import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { api } from '../services/api';
import Logo from '../components/Logo';

export default function Login() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  const setLoading = useAuthStore((state) => state.setLoading);

  // Development mode: pre-fill password with 'Foundation1!'
  const isDev = (import.meta as { env?: { MODE?: string } }).env?.MODE === 'development' || window.location.hostname === 'localhost';
  const defaultPassword = isDev ? 'Foundation1!' : '';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState(defaultPassword);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    setLoading(true);

    try {
      const response = await api.login(email, password);
      setAuth(response.user, response.access_token, response.refresh_token);
      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-cream flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-soft-lg p-8 w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <Logo size="lg" />
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-display font-bold text-primary-500 mb-2">
            Welcome Back!
          </h1>
          <p className="text-emerald-600">
            Login to create magical stories for your children
          </p>
          {isDev && (
            <div className="mt-3 p-2 bg-yellow-50 border border-yellow-300 rounded-lg">
              <p className="text-xs text-yellow-800">
                <strong>Dev Mode:</strong> Password pre-filled with 'Foundation1!'
              </p>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-semibold text-emerald-600 mb-2"
            >
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-field"
              placeholder="you@example.com"
              required
              autoComplete="email"
            />
          </div>

          <div>
            <label
              htmlFor="password"
              className="block text-sm font-semibold text-emerald-600 mb-2"
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-field"
              placeholder="••••••••"
              required
              autoComplete="current-password"
            />
          </div>

          <div className="flex items-center justify-between text-sm">
            <label className="flex items-center">
              <input
                type="checkbox"
                className="w-4 h-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="ml-2 text-emerald-600">Remember me</span>
            </label>
            <a
              href="/forgot-password"
              className="text-primary-500 hover:text-primary-600 font-semibold"
            >
              Forgot password?
            </a>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Logging in...' : 'Login'}
          </button>
        </form>

        {/* Divider */}
        <div className="my-6 flex items-center">
          <div className="flex-1 border-t-2 border-gray-200"></div>
          <span className="px-4 text-emerald-600 text-sm">or</span>
          <div className="flex-1 border-t-2 border-gray-200"></div>
        </div>

        {/* Register Link */}
        <div className="text-center">
          <p className="text-emerald-600">
            Don't have an account?{' '}
            <Link
              to="/register"
              className="text-primary-500 hover:text-primary-600 font-semibold"
            >
              Create one here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
